/* eslint-disable max-classes-per-file */

import { camelCase, pascalCase } from 'change-case';
import cloneDeep from 'clone-deep';
import { hasCaseManagerGroup } from 'khshared/cognitoUtils';
import { CampaignEventWithoutDerivedFields } from 'khshared/types/campaigns/CampaignEvent';
import { Logger } from 'pino';
import { useEffect, useRef } from 'react';
import { useHistory, useLocation, useParams } from 'react-router';
import { v4 as uuidv4 } from 'uuid';

import KHAuth from './KHAuth';
import useLogCampaignEvent from './useLogCampaignEvent';
import useLogger from './useLogger';

interface InternalWizardContext {
  wizardSessionID: string;
  wizardStartTime: string;
  wizardTimeSinceStartMilliseconds: number;
}

export type WizardContext = Omit<Record<string, unknown>, keyof InternalWizardContext>;

export interface Wizard<S extends WizardState, A extends WizardAction, C extends WizardContext, R> {
  name: string;
  defaultState: S;
  getDefaultContext: (defaultContextParams: GetDefaultContextParams<R>) => C;
  handleAction: (action: A, context: C & InternalWizardContext) => S;
  getLoggerFields: (context: C & InternalWizardContext, state?: S) => { [field: string]: unknown };
  getCampaignLoggerFields?: (
    context: C & InternalWizardContext,
  ) => () => Partial<CampaignEventWithoutDerivedFields>;
  fragments?: Record<string, unknown>;
}

type WizardStateName = `${string}-state`;
export interface WizardState {
  name: WizardStateName;
  props?: Record<string, unknown>;
}

type WizardActionName = `${string}-action`;
export interface WizardAction {
  wizardState: WizardState;
  name: WizardActionName;
  props?: Record<string, unknown>;
}

export type GetDefaultContextParams<R> = {
  routeParams: R;
  searchParams: URLSearchParams;
};

// Function to truncate the "-state" suffix.
function stripStateSuffix(stateName: string) {
  return stateName.slice(0, -6);
}
class UnexpectedWizardStateError extends Error {
  constructor(expectedStateName: string, actualState: unknown) {
    super(
      `Expected to be in wizard state ${expectedStateName} but instead received: ${JSON.stringify(
        actualState,
      )}`,
    );
    this.name = 'UnexpectedWizardStateError';
  }
}

export class UnexpectedWizardActionError extends Error {
  constructor(wizardAction: WizardAction) {
    super(`Unexpected wizard action: ${JSON.stringify(wizardAction)}`);
    this.name = 'UnexpectedWizardActionError';
  }
}

const WIZARD_STATE_KEY = 'wizardState';
const WIZARD_CONTEXT_KEY = 'wizardContext';

export function getWizardLocation<
  S extends WizardState,
  A extends WizardAction,
  C extends WizardContext,
  R
>(
  wizard: Wizard<S, A, C, R>,
  wizardState: S,
  wizardContext: C,
  isCaseManager?: boolean,
): {
  pathname: string;
  state: {
    [WIZARD_STATE_KEY]: S;
    [WIZARD_CONTEXT_KEY]: C;
  };
} {
  return {
    pathname: `${isCaseManager ? '/case-manager' : ''}/${wizard.name}/${wizardState.name.replace(
      /(-state)$/,
      '',
    )}`,
    state: cloneDeep({ [WIZARD_STATE_KEY]: wizardState, [WIZARD_CONTEXT_KEY]: wizardContext }),
  };
}

function addInternalWizardContext<C extends WizardContext, R>(
  getDefaultContext: (defaultContextParams: GetDefaultContextParams<R>) => C,
  defaultContextParams: GetDefaultContextParams<R>,
): C & InternalWizardContext {
  const wizardStartTime = new Date().toUTCString();
  return {
    ...cloneDeep(getDefaultContext(defaultContextParams)),
    wizardSessionID: uuidv4(),
    wizardStartTime,
    wizardTimeSinceStartMilliseconds: Date.now() - new Date(wizardStartTime).valueOf(),
  };
}

export function useWizardState<
  S extends WizardState,
  A extends WizardAction,
  C extends WizardContext,
  R,
  CSN extends string
>(
  wizard: Wizard<S, A, C, R>,
  currentStateName: CSN,
): Extract<S, { name: CSN }> & {
  context: C & InternalWizardContext;
  next: (action: A, historyOperation?: 'push' | 'replace') => void;
  logger: Logger;
} {
  const history = useHistory();
  const location = useLocation<
    | {
        [WIZARD_STATE_KEY]: Extract<S, { name: CSN }>;
        [WIZARD_CONTEXT_KEY]: C & InternalWizardContext;
      }
    | undefined
  >();

  const routeParams = useParams();
  const searchParams = new URLSearchParams(location.search);

  const stateStartTime = useRef<number | null>(null);
  const isCaseManager = hasCaseManagerGroup(KHAuth.getGroupsOrNull() ?? []);

  const wizardContextRef = useRef<C & InternalWizardContext>(
    location?.state?.[WIZARD_CONTEXT_KEY] ??
      addInternalWizardContext(wizard.getDefaultContext, {
        routeParams: routeParams as R,
        searchParams,
      }),
  );

  const getCampaignLoggerFields = wizard.getCampaignLoggerFields?.(wizardContextRef.current);

  const wizardState: Extract<S, { name: CSN }> | undefined = location?.state?.[WIZARD_STATE_KEY];
  const logger = useLogger({
    ...wizard.getLoggerFields(wizardContextRef.current, wizardState),
    ...(getCampaignLoggerFields != null ? getCampaignLoggerFields() : {}),
    pathname: location.pathname,
    page: location.pathname.replace(
      // Strip any UUIDs. The purpose of this is to allow for better grouping of pages in Kibana
      /[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}/gi,
      '[uuid]',
    ),
    wizardName: wizard.name,
    wizardSessionID: wizardContextRef.current.wizardSessionID,
    wizardStateName: stripStateSuffix(currentStateName),
    wizardStartTime: wizardContextRef.current.wizardStartTime,
    wizardTimeSinceStartMilliseconds: wizardContextRef.current.wizardTimeSinceStartMilliseconds,
  } as { [field: string]: unknown } & InternalWizardContext);

  const logCampaignEvent = useLogCampaignEvent(getCampaignLoggerFields);

  useEffect(
    () =>
      // This is super hacky, but there's no other (less hacky) way to intercept the back button
      // event *before* React Router has unmounted this component, lost the context, and
      // navigated to another page.
      history.block((_, action) => {
        if (action === 'POP') logger.info({ eventName: 'backButtonClick' });
      }),
    [history, logger],
  );

  function next(action: A, historyOperation: 'push' | 'replace' = 'push'): void {
    if (!action.name.endsWith('action')) {
      throw new UnexpectedWizardActionError(action);
    }

    logger.info({
      eventName: 'pageViewEnd',
      wizardTimeInStateMilliseconds: stateStartTime.current && Date.now() - stateStartTime.current,
    });

    logCampaignEvent({
      eventName: `${camelCase(wizard.name)}${pascalCase(
        stripStateSuffix(currentStateName),
      )}PageViewEnd` as const,
      timeSpentInStateMilliseconds:
        (stateStartTime.current && Date.now() - stateStartTime.current) ?? -1,
    });

    history[historyOperation](
      getWizardLocation(
        wizard,
        wizard.handleAction(action, wizardContextRef.current),
        wizardContextRef.current,
        isCaseManager,
      ),
    );
  }

  // This handles any weird behavior that could potentially mess up
  // wizardState e.g. routing to the middle of a page in the booking flow.
  // This attempts to recover by re-retreiving the default context & updating
  // the location state
  if (
    (wizardState == null && currentStateName !== wizard.defaultState.name) ||
    (wizardState != null && wizardState.name !== currentStateName) ||
    !currentStateName.endsWith('-state')
  ) {
    if (wizard.defaultState != null) {
      const clonedDefaultContext = addInternalWizardContext(wizard.getDefaultContext, {
        routeParams: routeParams as R,
        searchParams,
      });
      history.replace(
        getWizardLocation(
          wizard,
          cloneDeep(wizard.defaultState),
          clonedDefaultContext,
          isCaseManager,
        ),
      );
      // Technically, this is the wrong type and could throw, but this render is not really
      // salvageable at this point anyway without the requisite data.
      return {
        ...({ name: currentStateName, props: {} } as Extract<S, { name: CSN }>),
        context: clonedDefaultContext,
        next,
        logger,
      };
    }
    throw new UnexpectedWizardStateError(currentStateName, wizardState);
  }

  if (stateStartTime.current == null) {
    logger.info({ eventName: 'pageView' });

    logCampaignEvent({
      eventName: `${camelCase(wizard.name)}${pascalCase(
        stripStateSuffix(currentStateName),
      )}PageView` as const,
    });

    stateStartTime.current = Date.now();
  }

  return {
    ...(wizardState as Extract<S, { name: CSN }>),
    context: wizardContextRef.current,
    next,
    name: wizardState?.name ?? currentStateName,
    logger,
  };
}
